import React, {useState} from 'react'
import {Box, Stack, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {getGatewayCatalog, PAYMENT_COLORS} from './constants'
import {GatewayCardModel} from './types'
import GatewayStatus from './GatewayStatus'
import GatewayEnvironmentPill from './GatewayEnvironmentPill'
import GatewayMetrics from './GatewayMetrics'
import GatewayActions from './GatewayActions'

type Props = {
  card: GatewayCardModel
  metricsLoading?: boolean
  busy?: boolean
  onConnect: (card: GatewayCardModel) => void
  onManage: (card: GatewayCardModel) => void
  onDelete: (card: GatewayCardModel) => void
}

export default function GatewayCard({
  card,
  metricsLoading,
  busy,
  onConnect,
  onManage,
  onDelete,
}: Props) {
  const {t} = useTranslation()
  const catalog = getGatewayCatalog(card.factory_name)
  const [logoSrc, setLogoSrc] = useState(catalog?.logo || '')
  const brand = catalog?.brandColor || PAYMENT_COLORS.primary
  const connected = card.is_connected

  if (!catalog) {
    return null
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: '24px',
        borderRadius: '12px',
        backgroundColor: connected ? PAYMENT_COLORS.surface : PAYMENT_COLORS.background,
        border: connected ? `2px solid ${brand}` : `1px solid ${PAYMENT_COLORS.border}`,
        boxShadow: connected ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow 200ms ease',
        cursor: 'pointer',
        minHeight: 300,
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: 96,
          mb: 2,
          mx: '-8px',
          mt: '-8px',
          px: '8px',
          pt: '8px',
          borderRadius: '8px',
          backgroundColor: `${brand}08`,
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            backgroundColor: `${brand}26`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component='img'
            src={logoSrc}
            alt={t(catalog.nameKey) as string}
            onError={() => {
              if (catalog.logoFallback && logoSrc !== catalog.logoFallback) {
                setLogoSrc(catalog.logoFallback)
              }
            }}
            sx={{width: 64, height: 64, objectFit: 'contain'}}
          />
        </Box>
        <Box sx={{position: 'absolute', top: 8, insetInlineEnd: 8}}>
          <GatewayStatus connected={connected} />
        </Box>
      </Box>

      <Typography
        fontSize={16}
        fontWeight={600}
        color={connected ? PAYMENT_COLORS.textPrimary : PAYMENT_COLORS.textSecondary}
        mb='4px'
      >
        {t(catalog.nameKey) as string}
      </Typography>
      <Typography fontSize={13} color={PAYMENT_COLORS.textSecondary} mb={1.5}>
        {t(catalog.typeKey) as string}
      </Typography>

      {connected ? (
        <Stack direction='row' flexWrap='wrap' gap={1} mb={2}>
          {card.environments.sandbox ? (
            <GatewayEnvironmentPill
              environment='sandbox'
              active={card.environments.sandbox.is_active}
            />
          ) : null}
          {card.environments.live ? (
            <GatewayEnvironmentPill environment='live' active={card.environments.live.is_active} />
          ) : null}
        </Stack>
      ) : (
        <Typography fontSize={13} color={PAYMENT_COLORS.textMuted} mb={2}>
          {t(catalog.descriptionKey) as string}
        </Typography>
      )}

      {connected ? (
        <Box mb={2} minHeight={60}>
          <GatewayMetrics metrics={card.metrics} isLoading={metricsLoading} />
        </Box>
      ) : (
        <Box flex={1} />
      )}

      <Box mt='auto'>
        <GatewayActions
          connected={connected}
          busy={busy}
          onConnect={() => onConnect(card)}
          onManage={() => onManage(card)}
          onDelete={() => onDelete(card)}
        />
      </Box>
    </Box>
  )
}
